<script lang="ts">
import { defineComponent, PropType, toRef, ref, watch, onMounted } from 'vue'
import InputDefault from '@/components/form/InputDefault.vue'
import InputLeadFormWrapper from '@/components/lead/InputLeadFormWrapper.vue'
import { formDefaultProps, formPartial } from '@/models/formPartial'

export default defineComponent({
  name: 'InputFormPhoneNumber',

  components: {
    InputDefault,
    InputLeadFormWrapper,
  },

  props: {
    ...formDefaultProps,

    modelValue: {
      type: String,
      default: '',
    },

    header: {
      type: String,
      default: '電話番号'
    },

    placeholder: {
      type: String,
      default: '例：09012345678',
    },

    /**
     * autocomplete 属性に渡す値
     * @see https://developer.mozilla.org/ja/docs/Web/HTML/Attributes/autocomplete
     */
    autocomplete: {
      type: String as PropType<AutocompleteAttr>,
      default: 'tel',
    },

    /**
     * pattern 属性に渡す値
     * @see https://developer.mozilla.org/ja/docs/Web/HTML/Element/input#pattern
     */
    pattern: {
      type: String,
      default: '[0-9]*',
    },

    /**
     * inputmode 属性に渡す値
     * @see https://developer.mozilla.org/ja/docs/Web/HTML/Global_attributes/inputmode
     */
    inputmode: {
      type: String as PropType<'search' | 'text' | 'tel' | 'email' | 'none' | 'url' | 'numeric' | 'decimal' | undefined>,
      default: 'numeric',
    },

    /**
     * フォームのフォーカスアウト時に前後ホワイトスペースの trim するかどうか
     */
    hasTrim: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    // model
    const { hasError } = formPartial(props.name, toRef(props, 'validationScheme'))

    const localValue = ref('')

    watch(() => props.modelValue, () => {
      localValue.value = props.modelValue
    })

    onMounted(() => {
      localValue.value = props.modelValue
    })

    const onUpdate = (value: string | number) => {
      localValue.value = value
        .toString()
        .replace(/[\s-]+/g, '') // スペースやハイフンを削除
        .replace(/[０-９]/g, (m) => String.fromCharCode(m.charCodeAt(0) - 0xfee0))  // 全角を半角に変換

      emit('update:modelValue', localValue.value)
    }

    return {
      // data
      localValue,

      // computed
      hasError,

      // methods
      onUpdate,
    }
  }
})
</script>

<template>
  <InputLeadFormWrapper
    :model-value="modelValue"
    :header="header"
    :namespace="namespace"
    :name="name"
    :is-disabled="isDisabled"
    :validation-scheme="validationScheme">
    <InputDefault
      :model-value="localValue"
      type="tel"
      :name="name"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :pattern="pattern"
      :inputmode="inputmode"
      :has-trim="hasTrim"
      :is-disabled="isDisabled"
      :is-error="hasError"
      @update:model-value="onUpdate($event)" />

    <template #secondary>
      <slot name="secondary">
        <div class="o-note__general u-text__small u-margin__smaller">
          <p class="o-note__general--item">日中に繋がる携帯電話を推奨しています。</p>
        <!-- /.o-note__general --></div>
      </slot>
    </template>
  </InputLeadFormWrapper>
</template>
